// @ts-nocheck Use of goog.module and the yext namsepace
/**
 * Plugin used to manipulate the URL and browser history
 *
 * Anatomy of URL: https://www.yext.com/s/233703/example?id=123&q=test&#h1
 *
 * Scheme    https              Also called: protocol
 * Host      www.yext.com
 * Subdomain www
 * Domain    yext
 * TDL       com
 * Pathname  /s/233703/example
 * Query     ?id=123&q=test     Starts with ? and separates params with &
 * Fragment  #h1                Also called: hash, anchor
 *
 * URLs provided in methods should avoid supplying scheme and host information.
 * In other words, we would just want '/s/233703/example?id=123&q=test#h1'.
 * Furthermore, `window.history` does not allow manipulation to cross domain
 * locations.
 *
 * @author Adrian Mullings (amullings@yext.com)
 * @author Nicholas Cassleman (ncassleman@yext.com)
 */

goog.declareModuleId('yext.ui.UrlManipulator');

export class UrlManipulator {
  /**
   * @param {Window} w The window whose URL you want to manipulate
   * @param {UrlManipulator.UpdateMethod=} updateMethod
   */
  constructor(w, updateMethod) {
    this.w = w;
    this.updateMethod = updateMethod || UrlManipulator.UpdateMethod.HISTORY_REPLACE;
    if (!Object.values(UrlManipulator.UpdateMethod).includes(this.updateMethod)) {
      throw new TypeError('updateMethod must be of type UrlManipulator.UpdateMethod');
    }
  }

  /**
   * @param  {UrlManipulator.UpdateMethod=} updateMethod
   *
   * @return {UrlManipulator}
   */
  static fromCurrent(updateMethod) {
    return new UrlManipulator(window, updateMethod);
  }

  /**
   * Retrieves a value encoded as a param in the window's query string.
   * If the param is present multiple times in the URL, only the first
   * instance will be read.
   *
   * @param  {string} key
   *
   * @return {!Object}
   */
  getQueryParam(key) {
    let val = this.getUrlParam(key, this.w.location.href);
    if (!val) {
      return null;
    }
    try {
      return JSON.parse(decodeURIComponent(val));
    } catch (e) {
      return null;
    }
  }


  /**
   * Modifies the window's URL by adding or replacing params in the query
   * string
   *
   * @param  {string} key
   * @param  {!Object} value
   *
   * @return {UrlManipulator}
   */
  setQueryParam(key, value) {
    let loc = this.w.location;
    let params = this.getUrlParams(loc.href);

    params[key] = encodeURIComponent(JSON.stringify(value));

    let newUrl = `${loc.origin}${loc.pathname}`;
    newUrl = this.buildUrlWithParams(newUrl, params);
    newUrl = `${newUrl}${loc.hash}`;

    this.setWindowUrl(newUrl);

    return this;
  }

  /**
   * Clears a query param
   *
   * @param  {string} key The key of the query param to be removed
   *
   * @return {UrlManipulator}
   */
  removeQueryParam(key) {
    const queryContainsKey = new RegExp(`[?&]${key}=.+`).test(this.w.location.search);

    if (queryContainsKey) {
      const url = this.w.location.search
        .substr(1)
        .split('&')
        .filter(str => str.length > 0 && !str.startsWith(`${key}=`))
        .join('&')
        .replace(/(.+)/, '?$1');

      this.updateQuery(() => url);
    }

    return this;
  }

  /**
   * Replaces the window's current url with the given string
   *
   * @param  {string} url
   *
   * @return {UrlManipulator}
   */
  setWindowUrl(url) {
    switch (this.updateMethod) {
      case UrlManipulator.UpdateMethod.HISTORY_PUSH:
        this.w.history.pushState(this.w.history.state, '', url);
        break;
      case UrlManipulator.UpdateMethod.HISTORY_REPLACE:
        this.w.history.replaceState(this.w.history.state, '', url);
        break;
      case UrlManipulator.UpdateMethod.NAVIGATE:
        this.w.location.href = url;
        break;
    }

    return this;
  }

  /**
   * Updates the components of a URL
   *
   * @param  {function(string):string} pathnameUpdater Update pathname fn
   * @param  {function(string):string} queryUpdater    Update query fn
   * @param  {function(string):string} fragmentUpdater Update fragment fn
   *
   * @return {UrlManipulator}
   */
  updateUrl(pathnameUpdater, queryUpdater, fragmentUpdater) {
    // search = query, hash = fragment
    const {pathname, search, hash} = this.w.location;
    this.setWindowUrl(
      pathnameUpdater(pathname) +
      queryUpdater(search) +
      fragmentUpdater(hash));

    return this;
  }

  /**
   * Updates the current URL's pathname
   *
   * @param  {function(string):string} pathnameUpdater Function to modify current
   *                                                   pathname. Pathname must
   *                                                   begin with '/'
   *
   * @return {UrlManipulator}
   */
  updatePathname(pathnameUpdater) {
    this.updateUrl(pathnameUpdater, query => query, fragment => fragment);

    return this;
  }

  /**
   * Updates the current URL's query string
   *
   * @param  {function(string):string} queryUpdater Function to modify current
   *                                                query. Query string must
   *                                                begin with '?' and separate
   *                                                params with '&'
   *
   * @return {UrlManipulator}
   */
  updateQuery(queryUpdater) {
    this.updateUrl(pathname => pathname, queryUpdater, fragment => fragment);

    return this;
  }

  /**
   * Updates the current URL's fragment
   *
   * @param  {function(string):string} fragmentUpdater Function to modify current
   *                                                   fragment. Fragment must
   *                                                   begin with '#'
   *
   * @return {UrlManipulator}
   */
  updateFragment(fragmentUpdater) {
    this.updateUrl(pathname => pathname, query => query, fragmentUpdater);

    return this;
  }

  /**
   * Retrieve the value of a param from the url if present, otherwise
   * return null.
   * @param {string} name The param to search for in the url
   * @param {string=} url URL to use instead of the current window's
   *
   * copied over from js/yext/yext.js to ease closure migration
   */
  getUrlParam(name, url) {
    url = url || window.location.href;
    var results = new RegExp('[\\?&]' + name + '=([^&#]*)').exec(url);

    if (results == null) {
      return null;
    } else {
      return results[1] || null;
    }
  }

  /**
   * Return an object that contains properties for each parameter passed in via URL.
   * NOTE: No decoding is done by this function. If decoding is needed, be sure to do so to the values
   * of the returned object.
   * For URL Hash params, see ui/lib/url.js.
   *
   * @return {?Object.<string>} empty if no params; object with key=value&20pair for each param
   *
   * copied over from js/yext/yext.js to ease closure migration
   */
  getUrlParams() {
    const paramsObj = {};
    const paramsStr = window.location.search.substring(1);

    if (paramsStr.length > 0) {
      const paramsArr = paramsStr.split('&');

      for (let i = 0; i < paramsArr.length; i++) {
        const param = paramsArr[i].split('=');
        if (paramsObj.hasOwnProperty(param[0])) {
          if (!Array.isArray(paramsObj[param[0]])) {
            paramsObj[param[0]] = [paramsObj[param[0]]];
          }
          paramsObj[param[0]].push(param[1]);
        } else {
          paramsObj[param[0]] = param[1];
        }
      }
    }
    return paramsObj;
  }

  /**
   * Return a string from a paramsObject (map) that represents the new url
   * NOTE: No encoding is done here, and this function assumes the values of paramsObj
   * are already encoded to be put into the url. See serializeUrl for encoding
   *
   * @param {string} url The base URL upon which to append parameters
   * @param {!Object} params Object with key value pair for each parameter (already encoded)
   *
   * @return {string} Base URL with appended parameters (if any)
   *
   * copied over from js/yext/yext.js to ease closure migration
   */
  buildUrlWithParams(url, params) {
    if (typeof url !== 'string') {
      throw new TypeError('Invalid URL; must be of type `string`');
    }

    if (typeof params !== 'object' && !!params) {
      throw new TypeError('Could not build params; must be of type `object`');
    } else if (this.size(params) <= 0) {
      return url;
    }

    const assembledParams = [];
    for (const key in params) {
      if (Array.isArray(params[key])) {
        params[key].forEach(function(subVal) {
          assembledParams.push(key + '=' + subVal);
        });
      } else {
        assembledParams.push(key + '=' + params[key]);
      }
    }

    return url + '?' + assembledParams.join('&');
  }

  /**
   * Returns the total number of unique properties for a particular object
   * NOTE: Does not work recursively (only counts the first layer of properties)
   * @param {!Object} obj The object to get the size of
   * @returns {number} the size of the object
   *
   * copied over from js/yext/yext.js to ease closure migration
   */
  size(obj) {
    let size = 0;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        size ++;
      }
    }

    return size;
  }
}

/**
 * Enum representing how the page's url should be updated
 * HISTORY_PUSH: No page load, but appends to the browser history
 * HISTORY_REPLACE: Only changes the URL, no page loading or history changing
 *     involved
 * NAVIGATE: Loads the new url
 *
 * @enum {Symbol}
 */
UrlManipulator.UpdateMethod = {
  HISTORY_PUSH: Symbol('HISTORY_PUSH'),
  HISTORY_REPLACE: Symbol('HISTORY_REPLACE'),
  NAVIGATE: Symbol('NAVIGATE'),
};
